import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  Competition,
  CompetitionsService,
  LeaderboardAward,
  LeaderboardEntry,
  awardValue,
  awardsForPick,
  prizeDescriptionsFor,
} from '../../../core/competitions/competitions';

/** One pick's contribution to a contestant's score, for the expanded
 *  per-contestant breakdown row. */
interface PickBreakdown {
  pickId: number;
  personName: string;
  dateOfDeath: string | null;
  causeOfDeath: string | null;
  points: number;
  prizeDescriptions: string[];
  awards: LeaderboardAward[];
}

/** A leaderboard entry plus its awards regrouped for display: one row per
 *  pick that scored something, and a separate bucket for competition-wide
 *  awards (e.g. Grim Reaper) that aren't tied to any one pick. */
interface LeaderboardRow extends LeaderboardEntry {
  pickBreakdown: PickBreakdown[];
  otherAwards: LeaderboardAward[];
}

/** F-015 — read-only ranked standings for one competition. Reuses
 *  GET :id/leaderboard (already open to any member — owner, cohost,
 *  contestant, or site admin), the same endpoint the competition detail
 *  page uses to compute each viewer's own score breakdown.
 *
 *  Each row expands (click to toggle) into a per-pick breakdown so a
 *  contestant can see exactly where every point came from — their own or
 *  anyone else's. Multiple rows can stay expanded at once (Warwick,
 *  2026-09-06: "people will want to compare their scores to others"),
 *  hence a Set of expanded ids rather than a single-selection signal. */
@Component({
  selector: 'app-competition-leaderboard',
  imports: [RouterLink],
  templateUrl: './competition-leaderboard.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './competition-leaderboard.css',
})
export class CompetitionLeaderboard implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly competitions = inject(CompetitionsService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly competition = signal<Competition | null>(null);
  readonly rows = signal<LeaderboardRow[]>([]);
  readonly expandedIds = signal<ReadonlySet<number>>(new Set());
  readonly awardValue = awardValue;

  async ngOnInit(): Promise<void> {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      // Sequential — matches the single-await-per-request pattern used
      // elsewhere (e.g. CompetitionDetail.load()).
      const competition = await this.competitions.getOne(id);
      this.competition.set(competition);
      const entries = await this.competitions.getLeaderboard(id);
      this.rows.set(entries.map(toRow));
    } catch {
      this.errorMessage.set(
        'Could not load the leaderboard — check the link or try again.',
      );
    } finally {
      this.loading.set(false);
    }
  }

  toggle(contestantId: number): void {
    const next = new Set(this.expandedIds());
    if (next.has(contestantId)) {
      next.delete(contestantId);
    } else {
      next.add(contestantId);
    }
    this.expandedIds.set(next);
  }

  dateOnly(iso: string): string {
    return iso.slice(0, 10);
  }
}

function toRow(entry: LeaderboardEntry): LeaderboardRow {
  const pickIds = [
    ...new Set(
      entry.awards
        .map((a) => a.pickId)
        .filter((id): id is number => id !== null),
    ),
  ];

  const pickBreakdown = pickIds
    .map((pickId): PickBreakdown => {
      const awards = awardsForPick(entry.awards, pickId);
      const first = awards[0];
      return {
        pickId,
        personName: first.personName ?? 'Unknown',
        dateOfDeath: first.dateOfDeath,
        causeOfDeath: first.causeOfDeath,
        points: awards.reduce((sum, a) => sum + (a.points ?? 0), 0),
        prizeDescriptions: prizeDescriptionsFor(awards),
        awards,
      };
    })
    .sort((a, b) => b.points - a.points);

  return {
    ...entry,
    pickBreakdown,
    otherAwards: entry.awards.filter((a) => a.pickId === null),
  };
}
